import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Box, Button, Divider, Fab, Grid, InputAdornment, TextField, Typography } from '@mui/material'
import { DataGrid, GridRowModel } from '@mui/x-data-grid'
import { ArrowBackOutlined, KeyboardArrowRightOutlined, CheckOutlined, ReportProblem } from '@mui/icons-material'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { LanguageContext } from '../../../utilities/contexts/LanguageContext'
import { DigitalReturnsService } from '../../../utilities/services/DigitalReturnsService'
import { AuthContext } from '../../../utilities/contexts/AuthContext'
import { SnackbarContext } from '../../../utilities/contexts/SnackbarContext'
import { convertEpochToTimezoneFormat } from '../../../utilities/helpers/DateTimeFormatters'
import { isEmptyStr } from '../../../utilities/helpers/AuthHelper'
import language from '../../../language/language'
import Colors from '../../../assets/Colors'
import printImage from '../../../assets/ic-print.png'
import { ReturnDetailsStyles } from './ReturnDetailsStyles'
import { SignatureModal } from '../SignatureModal/SignatureModal'
import { PrintableReturn } from '../PrintableReturn/PrintableReturn'
import { useDigitalReturnsByStore } from '../../../queries/DigitalReturns/useDigitalReturnsByStore'
import { DigitalReturnDTO, ReturnPartDTO } from '../../../utilities/types/DigitalReturnTypes'

const ReturnDetails = () => {
    const navigate = useNavigate()
    const { currentLanguage } = useContext(LanguageContext)
    const { currentUser } = useContext(AuthContext)
    const { addSnack } = useContext(SnackbarContext)
    const windowSize = useRef([window.innerWidth, window.innerHeight])
    const { data: digitalReturns, refetch: refetchNow } = useDigitalReturnsByStore()
    const selectedReturnId = JSON.parse(useLocation().state.item)
    const [filteredReturn, setFilteredReturn] = useState<DigitalReturnDTO | null>(null)
    const [unsavedReturn, setUnsavedReturn] = useState<DigitalReturnDTO | null>(null)
    const [isReviewCompleted, setIsReviewCompleted] = useState<boolean>(false)
    const [currentStatus, setCurrentStatus] = useState<string>('')
    const [saveEnabled, setSaveEnabled] = useState<number | null>(null)
    const [rejectNote, setRejectNote] = useState<string | null>(null)

    const [signatureModalVisible, setSignatureModalVisible] = useState(false)
    const [selectedRow, setSelectedRow] = useState<any[]>([])

    useEffect(() => {
        if (digitalReturns) {
            const filtered: DigitalReturnDTO = digitalReturns.filter((item: any) => item.returnId === selectedReturnId)[0]
            setFilteredReturn(filtered)
            setIsReviewCompleted(!!filtered.returnPartDTOs?.every((parts: any) => parts.partStatus === 'VERIFIED' || parts.partStatus === 'REJECTED'))
            setCurrentStatus(filtered.returnStatus)
        }
    }, [digitalReturns, selectedReturnId])

    const handleGoBack = async () => {
        navigate(-1)
    }

    const renderBackButton = () => {
        return (
            <Grid xs={12} sx={ReturnDetailsStyles.gridDetail} item>
                <Button onClick={handleGoBack} sx={ReturnDetailsStyles.backButton} data-testid='go-back'>
                    <ArrowBackOutlined fontSize='medium' sx={ReturnDetailsStyles.backButtonArrow} />
                    <Typography data-testid='back_button' sx={ReturnDetailsStyles.backButtonText}>
                        {language[currentLanguage].back}
                    </Typography>
                </Button>
            </Grid>
        )
    }

    const renderTitle = () => {
        return (
            <>
                <Grid xs={12} sx={ReturnDetailsStyles.gridDetail} marginTop={1} item>
                    <Typography sx={ReturnDetailsStyles.title}>
                        {filteredReturn?.customerName}
                    </Typography>
                </Grid>
                <Grid xs={12} sx={ReturnDetailsStyles.gridDetail} marginTop={'0px'} item>
                    <Typography>
                        Return Number: <strong>#{filteredReturn?.returnId}</strong>
                    </Typography>
                </Grid>
            </>
        )
    }

    //TODO refactor as part of Finish Screen
    const renderContent = () => {
        return (
            <>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            <Fab size='small' sx={ReturnDetailsStyles.firstStage(filteredReturn?.returnStatus ?? '')} >
                                {filteredReturn?.returnStatus === 'DRIVER_RECEIVED' ? '1' : <CheckOutlined fontSize='small' />}
                            </Fab>
                        </Grid>
                        <Grid item padding={0}>
                            <Typography margin={'0px 10px'}>
                                {language[currentLanguage].returnStarted}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Divider color={filteredReturn?.returnStatus === 'PENDING_VERIFICATION' ? 'green' : ''} />
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail} >
                        <Typography marginTop='0px' marginLeft='50px' fontSize={'14px'}>
                            {filteredReturn?.returnStatus === 'DRIVER_RECEIVED' ? language[currentLanguage].returnStartedDesc : (filteredReturn?.updatedTimestamp ? convertEpochToTimezoneFormat(filteredReturn?.updatedTimestamp) : '')}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            {filteredReturn?.returnStatus === 'DRIVER_RECEIVED' &&
                                <Fab size='small' sx={ReturnDetailsStyles.fab}>
                                    2
                                </Fab>}

                            {filteredReturn?.returnStatus !== 'DRIVER_RECEIVED' &&
                                <Fab size='small' sx={ReturnDetailsStyles.secondStage(filteredReturn?.returnStatus ?? '')}>
                                    {filteredReturn?.returnStatus === 'PENDING_VERIFICATION' ? '2' : <CheckOutlined fontSize='small' />}
                                </Fab>}
                        </Grid>
                        <Grid item padding={'0px'}>
                            <Typography margin={'0px 10px'} color={filteredReturn?.returnStatus !== 'DRIVER_RECEIVED' ? '' : Colors.napaGreyDisabled2}>
                                {language[currentLanguage].partsArrived}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Divider color={filteredReturn?.returnStatus === 'VERIFIED' ? 'green' : ''} />
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail}>
                        <Typography marginTop='0px' marginLeft='50px' fontSize={'14px'}>
                            {filteredReturn?.returnStatus !== 'DRIVER_RECEIVED' && filteredReturn?.returnStatus === 'PENDING_VERIFICATION' ? language[currentLanguage].verifyParts : ''}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            {(filteredReturn?.returnStatus === 'PENDING_VERIFICATION' || filteredReturn?.returnStatus === 'DRIVER_RECEIVED') &&
                                <Fab size='small' sx={ReturnDetailsStyles.fab}>
                                    3
                                </Fab>}

                            {filteredReturn?.returnStatus !== 'PENDING_VERIFICATION' && filteredReturn?.returnStatus !== 'DRIVER_RECEIVED' &&
                                <Fab size='small' sx={ReturnDetailsStyles.thirdStage(filteredReturn?.returnStatus ?? '')}>
                                    {filteredReturn?.returnStatus === 'VERIFIED' ? '3' : <CheckOutlined fontSize='small' />}
                                </Fab>}
                        </Grid>
                        <Grid item padding={'0px'}>
                            <Typography margin={'0px 10px'} color={filteredReturn?.returnStatus === 'VERIFIED' ? '' : Colors.napaGreyDisabled2}>
                                {language[currentLanguage].partsReview}
                            </Typography>
                        </Grid>
                        <Grid item xs>
                            <Divider color={filteredReturn?.returnStatus === 'COMPLETED' ? 'green' : ''} />
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail}>
                        <Typography marginTop='0px' marginLeft='50px' fontSize={'14px'}>
                            {filteredReturn?.returnStatus === 'VERIFIED' ? language[currentLanguage].partsInspected : ''}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid xs={3} item>
                    <Grid container alignItems={'center'} marginTop={1}>
                        <Grid item padding={'0px'}>
                            <Fab size='small' sx={ReturnDetailsStyles.fab}>
                                4
                            </Fab>
                        </Grid>
                        <Grid item padding={'0px'}>
                            <Typography margin={'0px 10px'} color={Colors.napaGreyDisabled2}>
                                {language[currentLanguage].sendToTAMS}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid sx={ReturnDetailsStyles.gridDetail} >
                        <Typography>

                        </Typography>
                    </Grid>
                </Grid>
            </>
        )
    }

    const handleUpdateReturn = async () => {
        try {
            const requestStatus = filteredReturn?.returnStatus === 'DRIVER_RECEIVED' ? 'PENDING_VERIFICATION' : 'VERIFIED'
            const request = {
                returnId: filteredReturn?.returnId,
                returnStatus: requestStatus,
                updatedTimestamp: Date.now(),
                email: currentUser?.email ?? '',
                updatedBy: currentUser?.username ?? '',
                returnPartDTOs: filteredReturn?.returnPartDTOs?.map((parts) => ({
                    ...parts,
                    partStatus: filteredReturn?.returnStatus === 'DRIVER_RECEIVED' ? 'PENDING' : parts.partStatus,
                    updatedBy: currentUser?.username ?? ''
                }))
            }
            await DigitalReturnsService.updateReturn(request)
            refetchNow({ cancelRefetch: true })
            addSnack({
                severity: 'success',
                message: 'Successfully updated Return',
                action: null,
                duration: 3000
            })
        }
        catch (e) {
            addSnack({
                severity: 'error',
                message: 'Error',
                action: null,
                duration: 3000
            })
        }
    }

    const handleValidationPart = async (type: string) => {
        try {
            const isRejected = filteredReturn?.returnPartDTOs?.filter(item => selectedRow.some(part => item.partId === part && isEmptyStr(item.notes)))
            if (type === 'reject' && isRejected && isRejected?.length > 0) {
                setRejectNote(language[currentLanguage].notesRequired)
                addSnack({
                    severity: 'error',
                    message: language[currentLanguage].notesRequired,
                    action: null,
                    duration: 3000
                })
            }
            else {
                setRejectNote(null)
                const request = {
                    returnId: filteredReturn?.returnId,
                    updatedTimestamp: Date.now(),
                    email: currentUser?.email ?? '',
                    updatedBy: currentUser?.username ?? '',
                    returnPartDTOs: filteredReturn?.returnPartDTOs?.map(parts => ({
                        ...parts,
                        partStatus: selectedRow?.includes(parts.partId) ? (type === 'verify' ? 'VERIFIED' : 'REJECTED') : parts.partStatus,
                        updatedBy: currentUser?.username ?? ''
                    }))
                }

                const response = await DigitalReturnsService.updateReturn(request)
                setSelectedRow([])
                refetchNow({ cancelRefetch: true })
                addSnack({
                    severity: 'success',
                    message: response?.message ?? 'Successfully updated Return',
                    action: null,
                    duration: 3000
                })
            }
        }
        catch (e) {
            addSnack({
                severity: 'error',
                message: 'Error',
                action: null,
                duration: 3000
            })
        }
    }

    const ReturnDetailsGrid: ReactElement = useMemo(() => {

        const handleSaveRow = (params: any) => {
            if (unsavedReturn && saveEnabled) {
                setFilteredReturn(unsavedReturn)
                setSaveEnabled(null)
                setUnsavedReturn(null)
                setRejectNote(null)
            }
        }

        const getChangedFields = <ReturnPartDTO extends object>(newObj: ReturnPartDTO, oldRow: ReturnPartDTO): Partial<ReturnPartDTO> => {
            return (Object.keys(newObj) as (keyof ReturnPartDTO)[]).reduce<Partial<ReturnPartDTO>>((changed, key) => {
                const typedKey = key as keyof ReturnPartDTO
                if (newObj[typedKey] !== oldRow[typedKey]) {
                    changed[typedKey] = newObj[typedKey]
                }
                return changed
            }, {})
        }


        const handleProcessRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
            const hasChanged = Object.keys(newRow).some((key: any) => newRow[key] !== oldRow[key])

            if (hasChanged && filteredReturn) {
                setSaveEnabled(newRow.partId)
                setUnsavedReturn({
                    ...filteredReturn,
                    returnPartDTOs: filteredReturn?.returnPartDTOs?.map((part) => {
                        if (part.partId === newRow.partId) {
                            let changedField = getChangedFields(newRow, part)
                            if (changedField.isPartScanned)
                                changedField.isPartScanned = changedField.isPartScanned === 'Scanned' ? true : false
                            return { ...part, ...changedField }
                        }
                        return part
                    })
                })
            }
            return newRow
        }

        let statusDropdown = ['PENDING', 'VERIFIED', 'REJECTED', 'DRIVER RECEIVED']
        if (filteredReturn?.returnStatus !== 'DRIVER_RECEIVED') statusDropdown.pop()

        const columns = [
            {
                field: 'productLine',
                headerName: 'LINE',
                minWidth: 100,
                editable: true,
                filterable: false,
            },
            {
                field: 'partNumber',
                headerName: 'PART NUMBER',
                minWidth: 150,
                editable: true,
                filterable: false,
            },
            {
                field: 'description',
                headerName: 'DESCRIPTION',
                minWidth: 250,
                editable: true,
                filterable: false,
            },
            {
                field: 'qty',
                headerName: 'QTY',
                minWidth: 100,
                editable: true,
                type: 'number'
            },
            {
                field: 'partType',
                headerName: 'TYPE',
                minWidth: 100,
                editable: true,
                filterable: false,
            },
            {
                field: 'isPartScanned',
                headerName: 'ADDED',
                minWidth: 150,
                editable: true,
                filterable: false,
                type: 'singleSelect',
                valueOptions: ['Scanned', 'Manual']
            },
            {
                field: 'notes',
                headerName: 'NOTES',
                minWidth: 250,
                editable: true,
                filterable: false,
                renderCell: (params: any) => {
                    return (
                        <Box sx={{ border: '1px green dash', height: '100%', px: 1, width: '100%', alignItems: 'center' }}>
                            <TextField
                                variant='outlined'
                                sx={ReturnDetailsStyles.notesSection}
                                onChange={() => { if (selectedRow.includes(params.row.partId)) setRejectNote(null) }}
                                value={params.row.notes ?? ''}
                                error={selectedRow.includes(params.row.partId) && !params.row.notes && !!rejectNote}
                                InputProps={{
                                    endAdornment: selectedRow.includes(params.row.partId) && !params.row.notes && !!rejectNote ? (
                                        <InputAdornment position='end'>
                                            <ReportProblem color='error' />
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </Box>
                    )
                }
            },
            {
                field: 'partStatus',
                headerName: 'STATUS',
                minWidth: 200,
                editable: true,
                type: 'singleSelect',
                valueOptions: statusDropdown
            },
            {
                field: 'edit',
                headerName: '',
                minWidth: 50,
                editable: false,
                filterable: false,
                renderCell: (params: any) => {
                    return (
                        <Button
                            onClick={async (event: any) => {
                                event.stopPropagation()
                                handleSaveRow(params.row)
                            }}
                            disabled={!(saveEnabled && (saveEnabled === params.row.partId))}
                            sx={ReturnDetailsStyles.editButton}
                        >
                            {(saveEnabled && saveEnabled === params.row.partId) ? language[currentLanguage].save : language[currentLanguage].edit}
                        </Button>
                    )
                }
            },
        ]

        const renderDataGrid = () => {
            return (
                <DataGrid
                    getRowId={(row) => row.partId}
                    disableColumnMenu
                    style={{ height: `${windowSize.current[1] - 450}px` }}
                    disableColumnSelector={true}
                    hideFooterPagination={true}
                    rows={(saveEnabled ? unsavedReturn?.returnPartDTOs : filteredReturn?.returnPartDTOs)?.map((returns: ReturnPartDTO, index: number) => {
                        const row = {
                            productLine: returns.productLine,
                            partNumber: returns.partNumber,
                            description: returns.description,
                            qty: returns.qty,
                            partType: returns.partType,
                            isPartScanned: returns.isPartScanned ? 'Scanned' : 'Manual',
                            notes: returns.notes,
                            partStatus: returns.partStatus === 'DRIVER_RECEIVED' ? 'DRIVER RECEIVED' : returns.partStatus,
                            index: index,
                            partId: returns.partId
                        }
                        return row
                    }) || []}
                    loading={!filteredReturn}
                    columns={columns}
                    checkboxSelection={filteredReturn?.returnStatus !== 'DRIVER_RECEIVED'}
                    isCellEditable={(row) => !(filteredReturn?.returnStatus === 'DRIVER_RECEIVED') && (saveEnabled === null || saveEnabled === row.id)}
                    hideFooter
                    disableSelectionOnClick
                    processRowUpdate={handleProcessRowUpdate}
                    onSelectionModelChange={(selected) => {
                        setSelectedRow(selected)
                    }}
                    experimentalFeatures={{ newEditingApi: true }}
                    selectionModel={selectedRow}
                    hideFooterSelectedRowCount={false}
                    disableVirtualization={process.env.NODE_ENV === 'test'}
                    sx={ReturnDetailsStyles.gridSize}
                />
            )
        }
        return renderDataGrid()
    }, [filteredReturn, setFilteredReturn, setSelectedRow, selectedRow, saveEnabled, unsavedReturn, rejectNote, currentLanguage])

    const buttonText = () => {
        let value
        if (currentStatus === 'DRIVER_RECEIVED')
            value = language[currentLanguage].confirmParts
        else if (currentStatus === 'PENDING_VERIFICATION')
            value = language[currentLanguage].continueToReview
        else
            value = language[currentLanguage].sendToTAMS

        return value
    }

    return (
        <Grid container spacing={1} sx={ReturnDetailsStyles.gridContainer}>
            {renderBackButton()}
            {renderTitle()}
            {renderContent()}
            <Grid marginLeft={'auto'}>
                <Button
                    onClick={() => {
                        setSignatureModalVisible(true)
                    }}
                    sx={ReturnDetailsStyles.disableText}
                    style={ReturnDetailsStyles.viewSignatureButton}
                    data-testid='view-signature'>
                    {language[currentLanguage].viewSignature}
                </Button>
                {filteredReturn && <PDFDownloadLink
                    document={<PrintableReturn selectedReturn={filteredReturn} />}
                    fileName={`${selectedReturnId.toString()}.pdf`}>
                    <Button
                        sx={ReturnDetailsStyles.disableText}
                        style={ReturnDetailsStyles.printButton}
                        data-testid='print-slip'>
                        {language[currentLanguage].print}
                        <Avatar
                            aria-label='print-icon'
                            sx={ReturnDetailsStyles.printIcon}
                            src={printImage}
                            alt='print'></Avatar>
                    </Button>
                </PDFDownloadLink>}
            </Grid>
            <Grid item xs={12}>
                {ReturnDetailsGrid}
            </Grid>
            {filteredReturn && <SignatureModal
                isVisible={signatureModalVisible}
                closeCallback={() => {
                    setSignatureModalVisible(false)
                }}
                storeNumber={filteredReturn?.storeNumber}
                printName={filteredReturn?.createdBy}
                signatureImage={filteredReturn?.signature}
            />}
            {(filteredReturn?.returnStatus === 'PENDING_VERIFICATION' || filteredReturn?.returnStatus === 'VERIFIED') && <Grid item xs={12} sx={ReturnDetailsStyles.validateContainer}>
                <Button
                    onClick={() => handleValidationPart('reject')}
                    variant='secondary'
                    sx={ReturnDetailsStyles.rejectButton}
                    disabled={!(selectedRow && selectedRow?.length > 0) || !!saveEnabled}
                    data-testid='reject-returns'>
                    {language[currentLanguage].reject}
                </Button>
                <Button
                    onClick={() => handleValidationPart('verify')}
                    variant='primary'
                    sx={ReturnDetailsStyles.verifyButton}
                    disabled={!(selectedRow && selectedRow?.length > 0) || !!saveEnabled}
                    data-testid='verify-returns'>
                    {language[currentLanguage].verify}
                </Button>
            </Grid>}
            <Grid sx={{ marginLeft: 'auto', marginTop: '10px' }}>
                <Button
                    onClick={() => handleUpdateReturn()}
                    variant='primary'
                    disabled={filteredReturn?.returnStatus === 'DRIVER_RECEIVED' ? false : !isReviewCompleted}
                    data-testid='confirm-returns'>
                    {buttonText()}
                    <KeyboardArrowRightOutlined fontSize='large' />
                </Button>
                {filteredReturn?.returnStatus === 'PENDING_VERIFICATION' && !isReviewCompleted &&
                    <Typography sx={ReturnDetailsStyles.qTip}>
                        {language[currentLanguage].cannotContinue}
                    </Typography>}
            </Grid>
        </Grid>
    )
}

export default ReturnDetails
